import { FC } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  ColorOptionLabel,
  MiDetailFields,
  MiList,
} from "@miplatform/components";
import {
  CONFIGURABLE_COLORS,
  DATE_TIME_FORMATS,
  ENTITY_NAMES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { ICalendarSet, IOrderTemplateResponse } from "@miplatform/types";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IWorkOrderTemplateData } from "../ConfigWorkOrderTemplates.types";
import { ConfigAttachedOrderTemplateCard } from "../components/ConfigAttachedOrderTemplateCard";

export const WorkOrderDetailsTabView: FC<WorkOrderDetailsTabViewProps> = ({
  form,
  extraTabProps: { orderTemplates = [], calendarSets = [], isReadOnly } = {},
}) => {
  const {
    watch,
    setValue,
    control,
    trigger,
    formState: { errors },
  } = form;
  const { orderTemplateId, calendarCuttoffTime, atRiskLeadHours } = watch();

  const handleSelectOrderTemplate = (template: IOrderTemplateResponse) => {
    if (isReadOnly) return;

    setValue("orderTemplateId", template.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("orderTemplateName", template.name, {
      shouldDirty: true,
    });
    setValue("tradeId", template.tradeId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const workOrderDetailFields = [
    {
      label: `${ENTITY_NAMES.WORK_ORDER_TEMPLATE} Name`,
      name: "name",
      formControl: control,
      required: true,
      inputProps: { maxLength: 255 },
      disabled: isReadOnly,
      width: "25%",
    },
    {
      label: "Short Name",
      name: "shortName",
      formControl: control,
      required: true,
      maxLength: 8,
      disabled: isReadOnly,
      width: "25%",
    },
    {
      label: "Abbreviation",
      name: "abbreviation",
      formControl: control,
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        form.setValue("abbreviation", e.target.value.toUpperCase(), {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      required: true,
      maxLength: 3,
      disabled: isReadOnly,
      width: "25%",
    },
    {
      label: "Color",
      name: "color",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      disabled: isReadOnly,
      width: "25%",
    },
    {
      label: "Description",
      name: "description",
      formControl: control,
      disabled: isReadOnly,
      width: "50%",
    },
    {
      label: "Hours Before Due Date to Show At Risk",
      name: "atRiskLeadHours",
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      suffix: Number(atRiskLeadHours) === 1 ? " hour" : " hours",
      value: atRiskLeadHours,
      allowNegative: false,
      decimalScale: 0,
      InputProps: { inputProps: { maxLength: 10 } },
      setValue: (value: string) => {
        setValue("atRiskLeadHours", Number(value), {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
      disabled: isReadOnly,
      width: "50%",
    },
    {
      label: "Type Of Work Order",
      name: "workType",
      width: "100%",
      formControl: control,
      fieldType: INPUT_TYPES.RADIO_GROUP,
      options: [
        { label: "Office Work Order", value: "Office" },
        { label: "Field Work Order", value: "Field" },
      ],
    },
    {
      label: "custom",
      name: "workType",
      width: "100%",
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <Typography
          variant="body2"
          color="primary.main"
          fontWeight="600"
          fontSize={16}
        >
          Calendar Rules
        </Typography>
      ),
    },
  ];

  const calendarRulesFields = [
    {
      label: "Calendar Set",
      name: "calendarSetId",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: [
        { label: "None", value: null },
        ...calendarSets.map((calendarSet) => ({
          label: calendarSet.name,
          value: calendarSet.id,
        })),
      ],
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        const value =
          e.target.value === "" || e.target.value === "null"
            ? null
            : e.target.value;
        setValue("calendarSetId", value, {
          shouldDirty: true,
          shouldValidate: true,
        });
        trigger("calendarCuttoffTime");
      },
      disabled: isReadOnly,
      width: "50%",
      helperText: "Select which calendar set this work order belongs to",
    },
    {
      label: "Cutoff Time",
      name: "calendarCuttoffTime",
      fieldType: INPUT_TYPES.TIME_PICKER,
      format: DATE_TIME_FORMATS.FORMAT_24_HOUR,
      value: calendarCuttoffTime ?? null,
      handleChange: (formattedValue: string) => {
        const time = /^\d{2}:\d{2}$/.test(formattedValue)
          ? `${formattedValue}:00`
          : formattedValue;
        setValue("calendarCuttoffTime", time, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      disabled: isReadOnly,
      width: "50%",
      error: !!errors.calendarCuttoffTime,
      helperText: errors.calendarCuttoffTime?.message
        ? String(errors.calendarCuttoffTime.message)
        : "Specify the completion cutoff time for the due date.",
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ConfigAccordionHeader title="Work Order Details">
        <MiDetailFields
          detailFields={[...workOrderDetailFields, ...calendarRulesFields]}
        />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title={`Attach ${ENTITY_NAMES.ORDER_TEMPLATE}`}>
        <MiList
          viewType="card"
          data={orderTemplates}
          hideListPagination
          CardComponent={ConfigAttachedOrderTemplateCard}
          cardProps={{
            selectedId: orderTemplateId,
            onSelect: handleSelectOrderTemplate,
            isReadOnly,
          }}
          emptyListProps={{
            text: "No Order Types found",
          }}
        />
      </ConfigAccordionHeader>
    </Box>
  );
};

interface WorkOrderDetailsTabViewProps {
  form: UseFormReturn<IWorkOrderTemplateData>;
  extraTabProps?: {
    orderTemplates?: IOrderTemplateResponse[];
    calendarSets?: ICalendarSet[];
    isReadOnly?: boolean;
  };
}
